import type { ElementType } from "react";
import React from "react";
import { styled } from "goober";

const TagContainer = styled("span")<{
  as?: keyof JSX.IntrinsicElements;
  $textColor: string;
  $hoverColor: string;
}>`
  --radius: var(--br);
  --gap: var(--sp-1);
  --color: ${({ $textColor }) => $textColor};
  --icon-size: var(--sp-4);
  --hover-color: ${({ $hoverColor }) => $hoverColor};

  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--gap);
  font-family: "Inter", sans-serif;
  white-space: nowrap;
  color: var(--color);
  border-radius: var(--radius);

  &:hover {
    color: var(--hover-color);
  }

  > * {
    position: relative;
  }

  svg,
  img {
    height: var(--icon-size);
    width: var(--icon-size);
    display: block;
  }

  svg {
    fill: currentColor;
  }

  .icon-left {
    margin-left: calc(var(--sp-1) * -1.5);
  }
  .icon-right {
    margin-right: calc(var(--sp-1) * -1.5);
  }

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background: var(--color);
    opacity: 0.15;
    border-radius: var(--radius);
  }

  &[data-size="xs"] {
    --radius: var(--sp-1);
    --gap: var(--sp-0_5);
    --icon-size: var(--sp-3);

    padding: 0 var(--sp-1);
  }

  &[data-size^="sm"] {
    --radius: var(--sp-1);
    --icon-size: var(--sp-3_5);

    padding: calc(var(--sp-0_5) / 2) var(--sp-1_5);
  }

  &[data-size^="med"] {
    --icon-size: var(--sp-4);

    padding: var(--sp-0_5) var(--sp-2);
  }

  &[data-size="lg"],
  &[data-size="large"] {
    --icon-size: var(--sp-4_5);
    padding: var(--sp-1_5) var(--sp-2);
  }
`;

const typeClasses = {
  xs: "type-caption--bold",
  sm: "type-caption--bold",
  small: "type-caption--bold",
  med: "type-form--button",
  medium: "type-form--button",
  lg: "type-form--button",
  large: "type-form--button",
} as const;

type TagSize = keyof typeof typeClasses;

export interface BaseTagProps {
  as?: keyof JSX.IntrinsicElements;
  size?: TagSize;
  color?: string;
  hoverColor?: string;

  text: string;

  iconLeft?: React.ReactNode;
  iconRight?: React.ReactNode;

  className?: string;
}

export interface LinkTagProps extends BaseTagProps {
  as: "a";
  href: string;
}

export type TagProps = BaseTagProps | LinkTagProps;

export const Tag = ({
  as = "span",
  size = "med",
  color = "var(--primary)",
  hoverColor = color,
  text,
  iconLeft,
  iconRight,
  ...rest
}: TagProps & React.ComponentPropsWithoutRef<ElementType>) => {
  const typography = typeClasses[size];

  return (
    <TagContainer
      as={as}
      data-size={size.toLowerCase()}
      $textColor={color}
      $hoverColor={hoverColor}
      {...rest}
    >
      {iconLeft && <span className="icon-left">{iconLeft}</span>}
      <span className={typography}>{text}</span>
      {iconRight && <span className="icon-right">{iconRight}</span>}
    </TagContainer>
  );
};
